import React from 'react';
import { Alert, Box, Container } from '@mui/material';
import { usePageTitle } from '@/shared/context/PageTitleContext';
import { usePermission } from '@/shared/hooks/usePermission';
import CalendarPageSkeleton from './skeletons/CalendarPageSkeleton';
import { mergeSx } from '@/shared/ui/utils/mergeSx';
import { toolbarActionsSx } from './toolbarActionsSx';

export interface CalendarPageProps {
  title: string;
  permission?: string;
  permissionDeniedMessage?: string;
  /** Optional tabs row rendered above the toolbar (same chrome as ListPage). */
  tabList?: React.ReactNode;
  toolbarLeft?: React.ReactNode;
  toolbarRight?: React.ReactNode;
  /** Gap between items in the left toolbar cluster. Default 2 (16px). */
  toolbarLeftGap?: number;
  /** Period toggle / range controls below the main toolbar. */
  secondaryToolbar?: React.ReactNode;
  /** Optional left filter column (FilterPanel) beside calendar content. */
  filterPanel?: React.ReactNode;
  filterPanelOpen?: boolean;
  filterPanelSeparated?: boolean;
  loading?: boolean;
  /** Replaces the default calendar skeleton when `loading` is true. */
  loadingFallback?: React.ReactNode;
  error?: string | null;
  children: React.ReactNode;
  maxWidth?: 'lg' | 'xl';
}

const CalendarPage: React.FC<CalendarPageProps> = ({
  title,
  permission,
  permissionDeniedMessage = 'Bạn không có quyền truy cập trang này.',
  tabList,
  toolbarLeft,
  toolbarRight,
  toolbarLeftGap = 2,
  secondaryToolbar,
  filterPanel,
  filterPanelOpen = false,
  filterPanelSeparated = false,
  loading = false,
  loadingFallback,
  error,
  children,
  maxWidth = 'xl',
}) => {
  usePageTitle(title);
  const { hasPermission } = usePermission();

  const showToolbar = Boolean(toolbarLeft || toolbarRight);

  if (permission && !hasPermission(permission)) {
    return (
      <Container maxWidth={maxWidth}>
        <Alert severity="error" sx={{ mt: 4 }}>
          {permissionDeniedMessage}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        flex: '1 1 0',
        width: '100%',
        alignSelf: 'stretch',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {tabList && (
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: 'common.white',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Container maxWidth={maxWidth}>{tabList}</Container>
        </Box>
      )}
    <Container
      maxWidth={maxWidth}
      sx={{
        flex: '1 1 0',
        width: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {showToolbar && (
        <Box
          sx={mergeSx(
            {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              mt: 2,
              mb: 1,
              flexShrink: 0,
            },
            toolbarActionsSx,
          )}
        >
          <Box
            sx={{
              display: 'flex',
              gap: toolbarLeftGap,
              flexWrap: 'wrap',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
            }}
          >
            {toolbarLeft}
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {toolbarRight}
          </Box>
        </Box>
      )}

      {secondaryToolbar && (
        <Box
          sx={mergeSx(
            {
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mb: 1,
              flexShrink: 0,
            },
            toolbarActionsSx,
          )}
        >
          {secondaryToolbar}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          mb: 2,
          ...(filterPanelSeparated && {
            pb: 0,
          }),
        }}
      >
        {filterPanel}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            ...(!filterPanelSeparated && {
              '& > .MuiPaper-root': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                ...(filterPanelOpen && {
                  borderTopLeftRadius: 0,
                }),
              },
            }),
          }}
        >
          {loading ? (loadingFallback ?? <CalendarPageSkeleton />) : children}
        </Box>
      </Box>
    </Container>
    </Box>
  );
};

export default CalendarPage;
